import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { ApiError, StreamEndedError } from '@core/api/client';
import type { WaveKind } from '../lib/referenceChartTools';
import type { ConsultTarget } from '../lib/consultTargets';
import type { SavedChartLayout } from '../lib/chartLayout';
import type { ChartIndicatorInput } from '../lib/chartIndicators';

// Types mirror the BE `AiBacktestContracts` / `AiBacktestResults` (camelCase on the wire).

export interface ExtractedParams {
  name?: string | null;
  description?: string | null;
  symbols?: string[] | null;
  timeframePeriod?: number | null;
  timeframeUnit?: string | null;
  startingBalance?: number | null;
  startDate?: string | null; // yyyy-MM-dd
  endDate?: string | null;   // yyyy-MM-dd — optional test end
}

export interface ParsePromptResponse {
  dslJson: string;
  entryLogic: string;
  exitLogic: string;
  extracted: ExtractedParams;
  missingFields: string[];
  /** May be FRACTIONAL since 2026-08-13 (a half-price retry leaves e.g. 9.5) — render raw, never
   *  round or `toFixed(0)` it. Same for the `/credits` `balance`. */
  creditsRemaining: number;
  /** The deterministic chart↔strategy conflicts (2026-08-20) — non-blocking cards in the `ready`
   *  stage. Computed BE-side off the same sanitized chart the model was shown; empty when no chart
   *  rode along or nothing disagrees. */
  conflicts: StrategyConflict[];
  /** The stored id of this translation, its `v2_ai_outputs` row (surface `backtest-parse`), since 2026-10-29
   *  (FSB action A53): the "Report this reply" link's reference, never the text. Absent from an older server. */
  outputId?: string | null;
}

/**
 * One chart↔strategy conflict on a SUCCESSFUL parse (2026-08-20, the Aug-19 "resolve conflicts
 * between the AI chat prompt and chart markings" doc). Engine output, never the model's judgement:
 * the card explains the contradiction and asks the intention — it proposes nothing, edits nothing,
 * and never blocks `Build strategy` (the chart stays optional context).
 */
export interface StrategyConflict {
  kind: 'instrument' | 'direction' | 'unexplainedMarks' | 'missingDrawing';
  /** The plain-words statement of what disagrees. */
  title: string;
  /** The evidence sentence — how the conflict was established. */
  detail: string;
  /** The specific marks in question (same row the Mark-in-question tab renders); absent when the
   *  conflict is not about marks. */
  marks?: { id: string; side: string; reason: string; price: number; time: number }[];
}

const CONFLICT_KINDS: readonly string[] = ['instrument', 'direction', 'unexplainedMarks', 'missingDrawing'];
/** Mirrors the BE's own caps (≤6 marks per card; a handful of cards) so a bad payload can't flood
 *  the panel. Lives HERE rather than in `lib/clarification` because that module imports from this
 *  one — the reverse import would be a cycle. */
const MAX_CONFLICTS = 8;

/**
 * Defensive read of the parse response's `conflicts` (2026-08-20): the field is new, so a BE
 * deployed before it simply omits it — and a half-populated row would render "undefined @ NaN",
 * which reads as a bug in the very feature meant to build trust in what the engine noticed.
 * A row is kept only when it can be rendered IN FULL; never throws.
 */
export function sanitizeConflicts(value: unknown): StrategyConflict[] {
  if (!Array.isArray(value)) return [];
  const out: StrategyConflict[] = [];
  for (const item of value) {
    if (out.length >= MAX_CONFLICTS) break;
    if (typeof item !== 'object' || item === null) continue;
    const c = item as Record<string, unknown>;
    if (typeof c.kind !== 'string' || !CONFLICT_KINDS.includes(c.kind)) continue;
    if (typeof c.title !== 'string' || c.title.trim().length === 0) continue;
    if (typeof c.detail !== 'string') continue;
    const marks = Array.isArray(c.marks)
      ? (c.marks as unknown[]).flatMap(m => {
          if (typeof m !== 'object' || m === null) return [];
          const r = m as Record<string, unknown>;
          return typeof r.id === 'string' && (r.side === 'buy' || r.side === 'sell')
            && typeof r.reason === 'string'
            && typeof r.price === 'number' && Number.isFinite(r.price)
            && typeof r.time === 'number' && Number.isFinite(r.time)
            ? [{ id: r.id, side: r.side, reason: r.reason, price: r.price, time: r.time }]
            : [];
        })
      : undefined;
    out.push({
      kind: c.kind as StrategyConflict['kind'],
      title: c.title,
      detail: c.detail,
      ...(marks !== undefined && marks.length > 0 ? { marks } : {}),
    });
  }
  return out;
}

/**
 * The controlled "why here" vocabulary (2026-07-27). Every option maps onto something the DSL can
 * express, so a reason can never point at an inexpressible idea. Mirrors the BE `MarkReasons`.
 */
export const MARK_REASONS = [
  { value: 'maCross', label: 'Moving-average cross' },
  { value: 'rsiExtreme', label: 'RSI extreme' },
  { value: 'macdCross', label: 'MACD cross' },
  { value: 'doublePattern', label: 'Double top/bottom' },
  { value: 'priceLevel', label: 'Price level' },
  { value: 'other', label: 'Other' },
] as const;

export type MarkReason = typeof MARK_REASONS[number]['value'];

/**
 * An annotation the user placed on the reference chart (`ChartReferenceInput`).
 *
 * `skip` is a NEGATIVE example — "looks like my setup but I would not trade it". Counter-examples
 * are what let the BE find a *discriminator* (true of every marked bar, false of every skipped one)
 * rather than merely what the positives have in common, so they are first-class.
 *
 * `reason` is the single highest-signal field: it turns the model's job from inferring intent out of
 * price action into translating a stated one. `stopPrice`/`targetPrice` are exact levels, sent as
 * placed and converted to percentages BE-side.
 */
export interface ChartReferenceMark {
  time: number;   // Unix seconds — the marked bar
  side: 'buy' | 'sell' | 'skip';
  price: number;  // the marked bar's close (representative), for the AI's context
  reason?: MarkReason;
  stopPrice?: number;
  targetPrice?: number;
  /**
   * Tags on the FILL this mark came from (2026-08-07) — only ever set on caller-supplied marks (the
   * user's own trades), never on ones placed in the composer, which is why it is optional. The chart
   * shows them beside the arrow and lists them on hover.
   */
  tags?: string[];
}

/**
 * Optional structured chart the user annotated in the composer (2026-07-24). Sent WITH the prompt
 * so the BE's LLM can read the exact price action + the intended buy/sell points as text context —
 * NOT an image (user direction: "send information, not image"). Bounded to ≤500 bars on the FE (the
 * BE re-validates the cap). Mirrors the BE `ChartReferenceRequest`.
 */
/**
 * Per-kind tallies of the reference chart's annotations (2026-07-29). Drives the panel's
 * "On the chart" chips and the pre-translate hints — `reasonless` counts trade marks (not skips —
 * a negative example is defined by NOT being the setup) missing their "Why here?" reason.
 */
export interface ChartAnnotationCounts {
  buys: number;
  sells: number;
  skips: number;
  reasonless: number;
  levels: number;
  trendlines: number;
  waves: number;
  fibLevels: number;
  /** The notes the user wrote on the chart (2026-10-06, F3a). */
  notes: number;
}

export function countAnnotations(ref: ChartReferenceInput | null): ChartAnnotationCounts {
  const marks = ref?.marks ?? [];
  return {
    buys: marks.filter(m => m.side === 'buy').length,
    sells: marks.filter(m => m.side === 'sell').length,
    skips: marks.filter(m => m.side === 'skip').length,
    reasonless: marks.filter(m => m.side !== 'skip' && !m.reason).length,
    levels: ref?.priceLevels?.length ?? 0,
    trendlines: ref?.trendlines?.length ?? 0,
    // COUNTS, not labels (2026-08-08): one drawn Elliott count is one annotation, like one trendline,
    // so the chips agree with the chart's own annotations list.
    waves: ref?.waves?.length ?? 0,
    fibLevels: ref?.fibLevels?.length ?? 0,
    notes: ref?.notes?.length ?? 0,
  };
}

/**
 * The stored chart-context summary (2026-07-29, mirrors the BE `ChartContextSummary`): what the
 * results page's strategy card says about the chart the strategy was translated from. Counts only —
 * the OHLC tape is translation input, not something persisted per strategy.
 */
export interface ChartContextSummary {
  symbol: string;
  timeframePeriod: number;
  timeframeUnit: string;
  buys: number;
  sells: number;
  skips: number;
  priceLevels: number;
  trendlines: number;
  /** Elliott COUNTS since 2026-08-08 — the stored JSON key keeps its old name so strategies saved
   *  before the change still read. Rendered as "2 wave counts" on the results card. */
  waveLabels: number;
  fibLevels: number;
  /** The indicators on the chart (2026-10-05); absent on strategies saved before. */
  indicators?: number;
  /** The notes the user wrote on the chart (2026-10-06, F3a), the count only; absent before. */
  notes?: number;
}

/** Parse the detail's raw `chartContextJson` defensively — a bad row must not sink the page. */
export function parseChartContext(json: string | null | undefined): ChartContextSummary | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json) as ChartContextSummary;
    return typeof v?.symbol === 'string' ? v : null;
  } catch {
    return null;
  }
}

export interface ChartReferenceInput {
  symbol: string;          // dataSymbol
  timeframePeriod: number;
  timeframeUnit: string;   // minute | hour | day
  bars: { time: number; open: number; high: number; low: number; close: number }[];
  marks: ChartReferenceMark[];
  /** Horizontal lines the user drew — the ONLY freehand drawing the DSL can honour, since a level
   *  becomes a `price vs constant` comparison. The support/resistance label implies the DIRECTION
   *  of the intended rule, which a bare number cannot. */
  priceLevels?: { price: number; kind?: 'support' | 'resistance' }[];
  /** Trendlines, sent as DESCRIPTION only: the engine has no sloping reference, so the AI may use
   *  them to understand intent but must never fabricate a rule from them. */
  trendlines?: { time1: number; price1: number; time2: number; price2: number }[];
  /** Fibonacci retracement levels (2026-07-28) — the ratio and the exact PRICE it lands on. A fib
   *  level is a price constant, which the DSL can compare against directly; the sloping measure it
   *  came from is not sent, because that part is not computable. */
  fibLevels?: { ratio: number; price: number }[];
  /**
   * Elliott wave counts the user drew (2026-08-08, replacing the flat `waveLabels` list) — a
   * LABELLING tool, not a detector: the app never counts waves, it forwards the user's own count so
   * the AI can tell which move a mark belongs to. Description only, like trendlines.
   *
   * The wire carries the KIND and the pivots; the labels are derived from the kind on both sides
   * (`WAVE_TYPES` here, `WaveTypes` on the BE), so a point can never carry a label its own position
   * contradicts — and the triple combo's two X's survive, which a label-keyed list could not express.
   */
  waves?: { kind: WaveKind; points: { time: number; price: number }[] }[];
  /**
   * The indicators on the user's chart with their settings, in the DSL's vocabulary (2026-10-05, F1).
   * The user's own settings (owner decision F1-1); the BE computes their values with its own engine.
   * Absent when the chart has none, so a chart without indicators sends exactly what it did before.
   */
  indicators?: ChartIndicatorInput[];
  /**
   * The notes the user wrote on the chart (2026-10-06, F3a; owner decision F3-1): TradingView's Text and
   * Note drawings, on their bar, one line each, at most 12 of 280 characters. The first FREE TEXT from the
   * chart to reach the AI (the translation and the Consult chat), quoted to it as the user's own words.
   * Absent when the chart has none.
   */
  notes?: { time: number; price: number; text: string }[];
}

/** Args for a translation: the prompt plus the optional annotated reference chart. */
export interface ParsePromptArgs {
  prompt: string;
  /** Optional structured inputs (2026-07-31): the user's own entry/exit rules, fed to the LLM as
   *  labelled blocks alongside the freeform prompt. Parse-only — never persisted separately. */
  entryStrategy?: string | null;
  exitStrategy?: string | null;
  chartReference?: ChartReferenceInput | null;
  /** The strategy being MODIFIED (2026-08-31). Modify is half price — the BE VERIFIES the id belongs
   *  to the caller before discounting, so sending it is a claim the server checks, not a price the
   *  client sets. Absent when building from scratch, which costs the full credit. */
  strategyId?: string | null;
  /** The strategy's DESCRIPTION (2026-08-14). Sent so the model can mine it for rules — it was
   *  collected as a saved label only, so anything a user wrote there was silently ignored. */
  description?: string | null;
  // No `disclaimerVersion` since 2026-11-02: the AI terms are accepted once a day through
  // `useAiConsent` (`POST /api/v1/ai/consent`), and the server checks that acceptance before charging a
  // credit or calling the model (`400 ai.consent.required` otherwise).
  /** The half-price retry voucher from the last rejection (2026-08-13). Single-use: the parse sent
   *  with it costs 0.5 credit; a spent/expired/foreign one 400s with `retry_token.invalid` and
   *  charges nothing. Omit (sent as null) when none is held. */
  retryToken?: string;
  /** The user's answers to the model's pushback questions (2026-08-14), RAW: the question text and
   *  the chosen option (or typed Other text) verbatim — the BE folds them into the model's message.
   *  The FE ALSO folds each answer into `prompt` as a `Confirmed: "…" = …` line before sending, so
   *  the saved composite persists the confirmations. Omit (sent as null) outside an Approve. */
  clarificationAnswers?: { question: string; answer: string }[];
  /** The composer's answered products + timeframe (2026-08-20) — what the BE's post-answer conflict
   *  pass compares the annotated chart against. Optional: absent, those two comparisons are skipped. */
  symbols?: string[];
  timeframePeriod?: number;
  timeframeUnit?: string;
}

export interface SaveStrategyRequest {
  name: string;
  description: string;
  prompt: string;
  dslJson: string;
  entryLogic: string;
  exitLogic: string;
  symbols: string[];
  timeframePeriod: number;
  timeframeUnit: string;
  startingBalance: number;
  startDate: string;             // yyyy-MM-dd
  endDate?: string | null;       // optional test end — null = up to today (the default)
  /** The annotated chart the translation used (2026-07-29) — stored server-side as the summary the
   *  results card shows. Omit on update to keep the existing context (free re-runs don't touch it). */
  chartReference?: ChartReferenceInput | null;
  /** Maximum DAILY drawdown, percent of the day's opening equity (2026-08-03 request A). Reaching it
   *  flattens and takes no new entries until the next 17:00 New York reset. The BE injects this into
   *  the saved strategy, overriding anything the AI inferred — a stated risk limit must be the one
   *  that runs. Null/omitted = no daily limit (and leaves any value the AI read from the prompt). */
  maxDailyDrawdownPct?: number | null;
  /** Maximum OVERALL drawdown, percent of peak equity (2026-08-03 request B). Reaching it closes
   *  everything and ENDS the run, unlike the daily limit which only benches that day. */
  maxDrawdownPct?: number | null;
  /** Implied slippage per fill, PERCENT OF PRICE (2026-08-03 request C). Applied adversely to entries
   *  and exits alike, including stops and take-profits. Null = frictionless. */
  slippagePct?: number | null;
  /** 2026-08-04 units. Each limit travels in exactly ONE unit — a percent, or an absolute amount of
   *  account currency (and for slippage, a price offset or a tick count). The BE rejects a
   *  combination rather than picking a winner, so whichever the user chose is the one that runs. */
  /** Whole lots per trade (2026-08-05 question 3A) — the BE maps it to sizing.fixedUnits. Null =
   *  the engine's default, which invests the full balance at every entry. */
  volumePerTrade?: number | null;
  maxDailyDrawdownAmount?: number | null;
  maxDrawdownAmount?: number | null;
  slippagePrice?: number | null;
  slippageTicks?: number | null;
  /** The two dedicated rule boxes as typed (2026-08-16). They still ride INSIDE `prompt` as the
   *  folded `Entry:`/`Exit:` lines — that composite is what the model reads — but they are now sent
   *  on their own as well so Modify can refill the boxes instead of reopening them empty. Null = the
   *  box is empty; both are REPLACED on every save, so clearing one persists. */
  entryStrategy?: string | null;
  exitStrategy?: string | null;
  /** The annotated chart itself (2026-10-05), so Modify reopens it as drawn. Sent ONLY with a fresh
   *  translation, like `chartReference`'s purpose: omitted, the BE keeps the saved one. Never sent to
   *  the AI. */
  chartLayout?: SavedChartLayout | null;
}

export interface StrategySavedResponse { strategyId: string; runId: string }

export interface StrategyRow {
  id: string;
  name: string;
  symbols: string[];
  createdAt: string;
  updatedAt: string;
  /** Head run, null until the strategy has run once — lets the saved-strategies list's "Result"
   *  button open a run without a per-row detail fetch (BE join added 2026-07-27). */
  latestRun?: RunSummary | null;
}

export interface RunSummary {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  error?: string | null;
  createdAt: string;
  finishedAt?: string | null;
}

export interface StrategyDetail {
  id: string;
  name: string;
  description: string;
  prompt: string;
  dslJson: string;
  entryLogic: string;
  exitLogic: string;
  symbols: string[];
  timeframePeriod: number;
  timeframeUnit: string;
  startingBalance: number;
  startDate: string;
  endDate?: string | null;       // optional test end — null = up to today
  createdAt: string;
  updatedAt: string;
  runCount: number;
  latestRun?: RunSummary | null;
  /** Raw stored chart-context JSON — parse with `parseChartContext`. */
  chartContextJson?: string | null;
  /** The user's own ENTRY / EXIT rule boxes, verbatim (2026-08-16) — what Modify refills the two
   *  dedicated boxes with. Null on every row saved before the columns existed, and on a box the
   *  user left blank; `prompt` still carries the folded copy either way. */
  entryStrategy?: string | null;
  exitStrategy?: string | null;
  /** The run parameters the questions set, read back out of the stored DSL by the BE (2026-09-02:
   *  Modify walks the same editable chips as creation, so the Volume / Daily DD / Max DD / Slippage
   *  chips must show what the strategy really runs with). Null/absent = not set. Same names and
   *  units as `StrategySaveBody`; exactly one unit per limit is ever non-null. */
  volumePerTrade?: number | null;
  maxDailyDrawdownPct?: number | null;
  maxDailyDrawdownAmount?: number | null;
  maxDrawdownPct?: number | null;
  maxDrawdownAmount?: number | null;
  slippagePct?: number | null;
  slippagePrice?: number | null;
  slippageTicks?: number | null;
  /** The chart saved with the strategy (2026-10-05), as stored: read it with `parseSavedChartLayout`
   *  (a layout this build cannot use reopens an empty chart). Absent when none was saved. */
  chartLayout?: unknown;
}

export interface AiEquityPoint { time: number; equity: number }

export interface AiBacktestPosition {
  positionId: string;
  symbol: string;
  side: 'long' | 'short';
  qty: number;
  openedAt: number;
  closedAt?: number | null;
  entryPrice: number;
  exitPrice?: number | null;
  exitReason?: string | null;
  grossPnl: number;
  netPnl: number;
  status: 'win' | 'loss' | 'breakeven' | 'open';
  barsHeld: number;
}

export interface AiChartMarker {
  time: number;
  side: 'buy' | 'sell';
  price: number;
  // entry | signalExit | stopLoss | takeProfit | accountTakeProfit | drawdownStop | reverseExit | reverseEntry
  // | levelEntry | levelExit (touch fills at a day level, 2026-10-21); a kind the chart does not know is
  // labelled by its side, so a new one never breaks it.
  kind: string;
  symbol: string;
}

export interface AiDirectionStats {
  positions: number;
  netPnl: number;
  maxDrawdown: number;
  avgDrawdown?: number | null;
  recoveryFactor?: number | null;
}

export interface AiBacktestSummary {
  netPnl: number;
  grossProfit: number;
  grossLoss: number;
  profitFactor?: number | null;
  wins: number;
  losses: number;
  scratches: number;
  winRate?: number | null; // 0..1
  positionCount: number;
  openPositions: number;
  avgPositionPnl?: number | null;
  avgWin?: number | null;
  avgLoss?: number | null;
  pnlRatio?: number | null;
  largestWin?: number | null;
  largestLoss?: number | null;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
  avgHoldSeconds?: number | null;
  startingBalance: number;
  endEquity: number;
  peakEquity: number;
  maxDrawdown: number;
  maxDrawdownPct?: number | null; // 0..1
  returnPct?: number | null;      // 0..1
  /** Mark-to-market P&L of positions still OPEN on the final bar (2026-08-03). Net P&L is
   *  realized-only while the equity curve is mark-to-market, so this is exactly the difference:
   *  `endEquity === startingBalance + netPnl + openPnl`. Zero when the run ended flat. */
  openPnl: number;
  long: AiDirectionStats;
  short: AiDirectionStats;
  halted: boolean;
  haltReason?: string | null;
}

export interface AiBacktestResults {
  summary: AiBacktestSummary;
  equity: AiEquityPoint[];
  positions: AiBacktestPosition[];
  markers: AiChartMarker[];
  warnings: string[];
}

export interface RunDetail {
  id: string;
  strategyId: string;
  status: RunSummary['status'];
  error?: string | null;
  createdAt: string;
  finishedAt?: string | null;
  results?: AiBacktestResults | null;
  /** The run's own snapshot (2026-07-29): prompt + parameters AS THEY WERE when the run queued —
   *  the strategy card follows the selected history run through this, so modifying the strategy
   *  can't rewrite what an old run claims it tested. `prompt` is null on pre-feature rows. */
  params?: {
    symbols: string[];
    timeframePeriod: number;
    timeframeUnit: string;
    startingBalance: number;
    startDate: string;
    endDate?: string | null;
    prompt?: string | null;
    entryLogic?: string | null;
    exitLogic?: string | null;
  } | null;
}

const ROOT = ['ai-backtest'] as const;
export const aiBacktestKeys = {
  root: ROOT,
  credits: [...ROOT, 'credits'] as const,
  strategies: [...ROOT, 'strategies'] as const,
  strategy: (id: string) => [...ROOT, 'strategies', id] as const,
  run: (id: string) => [...ROOT, 'runs', id] as const,
  // Prop-firm rule-template evaluation of one run (2026-08-06, @features/prop-eval). Under the
  // ai-backtest root so a strategy update / re-run (which invalidates root) refetches it too.
  propEval: (runId: string) => [...ROOT, 'runs', runId, 'prop-eval'] as const,
  // Nested under the strategy on purpose: a flat [...ROOT,'runs',id] would be indistinguishable
  // from `run(id)` above, so invalidating one would clobber the other.
  runsFor: (strategyId: string) => [...ROOT, 'strategies', strategyId, 'runs'] as const,
  // The conversation about a strategy (2026-09-02). Nested under the strategy so a strategy
  // invalidation refetches it too.
  chatTurns: (strategyId: string) => [...ROOT, 'strategies', strategyId, 'chat'] as const,
};

/** `opts.enabled` (default `true`) — added 2026-08-13 so `BacktestingPage` can gate the fetch itself
 * on `isPro`, mirroring the prop-eval cards' `enabled: on && isPro && …` pattern
 * (`usePropFirmEval`/`useDashboardPropEval`) instead of firing unconditionally and 403ing for a
 * core-tier caller before the `!isPro` render branch ever shows `ProUpsell`. */
export function useAiCredits(opts: { enabled?: boolean } = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: aiBacktestKeys.credits,
    enabled: opts.enabled ?? true,
    queryFn: () => api.get<{ balance: number; requestPending: boolean }>('/api/v1/ai-backtest/credits'),
  });
}

/**
 * "Request more credits" (2026-07-27) — the server mails support in the background, so the user
 * never leaves the page and never meets a mailto. The request is recorded server-side, so
 * `requestPending` survives a refresh and clears itself once credits are granted — hence the
 * credits invalidation on success.
 *
 * ⚠️ **NO UI CALLS THIS SINCE 2026-08-15.** The "Request more credits" / "Credits Requested" button
 * was removed on user request; buying a credit pack is the only top-up path. The hook, the endpoint
 * and the `requestPending` field are all kept so the free ask can be restored cheaply — do not
 * delete them assuming they are dead, and do not wire this up again without asking.
 */
export function useRequestCredits() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/v1/ai-backtest/credits/request', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.credits }),
  });
}

/** `opts.enabled` (default `true`) — see `useAiCredits`'s doc comment; same 2026-08-13 gating. */
export function useAiStrategies(opts: { enabled?: boolean } = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: aiBacktestKeys.strategies,
    enabled: opts.enabled ?? true,
    queryFn: () => api.get<StrategyRow[]>('/api/v1/ai-backtest/strategies'),
  });
}

export function useAiStrategy(id: string | undefined) {
  const api = useApiClient();
  return useQuery({
    queryKey: aiBacktestKeys.strategy(id ?? ''),
    enabled: !!id,
    queryFn: () => api.get<StrategyDetail>(`/api/v1/ai-backtest/strategies/${id}`),
  });
}

/** Poll ceiling: keep the 1.5s poll while queued/running, but stop after MAX_POLL_MS so a genuinely
 * stuck run doesn't poll forever (the realtime completion push still invalidates the query). Pure. */
export const MAX_POLL_MS = 5 * 60_000;
export function runPollInterval(
  status: RunSummary['status'] | undefined,
  startedAt: number,
  now: number,
): number | false {
  const active = status === 'queued' || status === 'running';
  if (!active) return false;
  if (now - startedAt >= MAX_POLL_MS) return false;
  return 1500;
}

/** Run detail — polls every 1.5s while queued/running, up to MAX_POLL_MS (the realtime completion push
 * also invalidates, so polling is just the belt-and-braces fallback). */
export function useAiRun(runId: string | undefined) {
  const api = useApiClient();
  const startedAtRef = useRef(Date.now());
  useEffect(() => {
    startedAtRef.current = Date.now(); // reset the poll window when a new run is watched
  }, [runId]);
  return useQuery({
    queryKey: aiBacktestKeys.run(runId ?? ''),
    enabled: !!runId,
    queryFn: () => api.get<RunDetail>(`/api/v1/ai-backtest/runs/${runId}`),
    refetchInterval: (query) => runPollInterval(query.state.data?.status, startedAtRef.current, Date.now()),
  });
}

/** One AI prompt (costs 1 credit). 409 = out of credits; 400 carries the model's clarification. */
/**
 * Every run of one strategy, newest first — the results page's "Backtest history" panel
 * (2026-07-25). Metadata only; the KPI comparison loads a selected run's results through
 * `useAiRun`. Disabled until a strategyId is known.
 */
export function useAiRuns(strategyId: string | undefined) {
  const api = useApiClient();
  return useQuery({
    queryKey: aiBacktestKeys.runsFor(strategyId ?? ''),
    enabled: !!strategyId,
    queryFn: () => api.get<RunSummary[]>(`/api/v1/ai-backtest/strategies/${strategyId}/runs`),
  });
}

// The server-owned AI disclaimer document (`useAiDisclaimer`, `GET /api/v1/ai-backtest/disclaimer`,
// 2026-08-03 to 2026-11-02) is gone from here: the AI terms, their sentence and THE OPTIMUS JOURNAL
// DISCLOSURE are read from `GET /api/v1/ai/consent` (`@features/ai-consent`), once a day, for every AI
// surface. The same disclaimer endpoint is still read, read-only, by the AI footers' `Read the AI
// disclaimer` (`useAiDisclosureDocument` in `@features/ai-consent`, 2026-11-03).

export function useParsePrompt() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prompt, entryStrategy, exitStrategy, chartReference, retryToken, clarificationAnswers, description, symbols, timeframePeriod, timeframeUnit, strategyId }: ParsePromptArgs) =>
      // The /parse Bedrock LLM call is legitimately slow — give it a long timeout, not the 20s default.
      api.post<ParsePromptResponse>(
        '/api/v1/ai-backtest/parse',
        {
          prompt,
          entryStrategy: entryStrategy ?? null,
          exitStrategy: exitStrategy ?? null,
          chartReference: chartReference ?? null,
          retryToken: retryToken ?? null,
          clarificationAnswers: clarificationAnswers ?? null,
          description: description ?? null,
          symbols: symbols ?? null,
          timeframePeriod: timeframePeriod ?? null,
          timeframeUnit: timeframeUnit ?? null,
          strategyId: strategyId ?? null,
        },
        { timeoutMs: 90_000 },
      // Sanitized at the point of storage: an older BE omits `conflicts` entirely, and a
      // half-populated row must be dropped rather than rendered — see `sanitizeConflicts`.
      ).then(r => ({ ...r, conflicts: sanitizeConflicts((r as { conflicts?: unknown }).conflicts) })),
    // onSettled, not onSuccess (2026-08-13 review finding F1): a REJECTED parse also charged — 1,
    // or 0.5 on a retry — so the chip and the credit gates must refetch the truth on both paths,
    // or a user at exactly 1 credit sees a stale "1" while the next click is a guaranteed 409.
    onSettled: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.credits }),
  });
}

/** One free-text chat turn about the strategy and its chart (2026-08-20, 0.5 credit). */
export interface StrategyChatArgs {
  message: string;
  chartReference?: ChartReferenceInput | null;
  strategyPrompt?: string | null;
  entryLogic?: string | null;
  exitLogic?: string | null;
  /** The conflict cards on screen, so the reply can address the exact contradiction. */
  conflicts?: StrategyConflict[];
  // No `disclaimerVersion` since 2026-11-02 (the once-per-session tick of 2026-08-20 is gone): the AI
  // terms are accepted once a day through `useAiConsent`, and the server checks that acceptance before
  // any turn runs.
  /** The strategy this conversation belongs to (2026-08-31). Its turns are persisted server-side and
   *  replayed to the model, so a follow-up ("and the exit?") has something to follow up on. Omitted
   *  before a strategy is saved — that chat stays session-only. */
  strategyId?: string | null;
  /** The run on screen (2026-08-31): the BE loads its KPIs and a trade summary into the model's
   *  context, so a question about results has results to read. Omitted pre-run. */
  runId?: string | null;
  /** Every value a proposal may change, as the form holds it RIGHT NOW (2026-09-07) — each in its own
   *  wire format. The model can only propose a target it can see, and it quotes the old value beside
   *  the new one, which is what makes the card's before/after true. */
  fields?: StrategyChatFields | null;
}

/** One value per proposal target, in that target's wire format (2026-09-07). */
export interface StrategyChatFields {
  /** The main "Describe your strategy" box. */
  mainBox?: string | null;
  description?: string | null;
  entry?: string | null;
  exit?: string | null;
  name?: string | null;
  /** Product codes, comma-separated. */
  products?: string | null;
  /** An app timeframe value, e.g. `15:minute`. */
  timeframe?: string | null;
  /** Digits only. */
  balance?: string | null;
  from?: string | null;
  /** `YYYY-MM-DD` or `today`. */
  to?: string | null;
  /** Digits, or `full`. */
  volume?: string | null;
  /** `5%` | `$500` | `none`. */
  dailyDrawdown?: string | null;
  maxDrawdown?: string | null;
  /** `0.5%` | `2 ticks` | `0.25 price` | `none`. */
  slippage?: string | null;
}

/**
 * A change the model believes the user just asked for (2026-08-31). It is a QUESTION, not an edit:
 * nothing moves until the user approves it, and the composer — which owns these boxes — is what
 * applies it. There is still no server-side edit channel.
 */
export interface StrategyChatProposalChange {
  /** What the user wants changed — a text box or a run parameter (2026-09-07). */
  target: ConsultTarget;
  /** A box's proposed new text in the user's own terms, or a parameter's new value in that
   *  parameter's own wire format (`30:minute`, `25000`, `full`, `5%`, `2 ticks`). */
  replacement: string;
}

export interface StrategyChatProposal {
  /** The ONE approval question covering every change, phrased as a question — never as advice. */
  question: string;
  /** 1–6 rewrites, at most one per target (2026-09-06: several boxes; 2026-09-07: parameters too). */
  changes: StrategyChatProposalChange[];
}

/** One question the model asks before it can propose (2026-09-06) — rendered as the strategy
 *  builder's radio-plus-Other form; an empty `options` is a free-text question. */
export interface ChatQuestion {
  text: string;
  options: string[];
}

export interface StrategyChatResponse {
  reply: string;
  creditsRemaining: number;
  /** Present only when the model read the message as an intention to change the strategy. */
  proposal?: StrategyChatProposal | null;
  /** Everything the model needs answered before it can propose, asked at once (2026-09-06). */
  questions?: ChatQuestion[] | null;
  /** With no saved strategy to hang off (no `turnId`), the `v2_ai_outputs` row that keeps the reply, since
   *  2026-10-29 (FSB action A53): the "Report this reply" link's reference. Null whenever `turnId` is set. */
  outputId?: string | null;
}

/** The final `reply` event of a streamed turn (2026-09-02): the JSON shape plus the persisted turn's
 *  id (null when the conversation had no strategy). Since 2026-09-25 it carries no reasoning: the
 *  Consult chat shows none, live or on replay (owner decision). */
export interface StrategyChatStreamResponse extends StrategyChatResponse {
  turnId?: string | null;
}

/** One persisted exchange, as `GET strategies/{id}/chat` replays it (oldest first). No reasoning
 *  field since 2026-09-25, for any turn, older ones included. */
export interface ChatTurnRecord {
  id: string;
  question: string;
  reply: string;
  proposal?: StrategyChatProposal | null;
  /** Per-box answers (2026-09-06): `{ entry: 'applied', exit: 'declined' }`; null while none. */
  decisions?: ChatDecisions | null;
  questions?: ChatQuestion[] | null;
  createdAt: string;
}

export type ChatDecision = 'applied' | 'declined';
/** One answer per box the proposal changed — each tab of the card is approved on its own. */
export type ChatDecisions = Partial<Record<StrategyChatProposalChange['target'], ChatDecision>>;

function chatBody(args: StrategyChatArgs) {
  return {
    message: args.message,
    chartReference: args.chartReference ?? null,
    strategyPrompt: args.strategyPrompt ?? null,
    entryLogic: args.entryLogic ?? null,
    exitLogic: args.exitLogic ?? null,
    conflicts: args.conflicts ?? null,
    strategyId: args.strategyId ?? null,
    runId: args.runId ?? null,
    fields: args.fields ?? null,
  };
}

/**
 * Ask the AI, streamed (2026-09-02): the promise resolves with the reply once the `reply` event
 * lands. The stream carries no reasoning since 2026-09-25 (owner decision: the Consult chat shows
 * none); only `reply` and `error` are read, so a `heartbeat` (sent through a long silence) and any
 * stray `reasoning` event are ignored. A server-side failure
 * after the stream started arrives as an `error` event and rejects with an ApiError carrying its
 * detail; a stream that ends without a reply rejects with StreamEndedError (the BE charged nothing).
 * Not a React Query mutation — a stream has no single result to cache — but the credits chip is
 * refetched on settle exactly as before. The reply EXPLAINS and asks; it never recommends. Since
 * 2026-08-31 it MAY propose a change the user asked for — a proposal is a question awaiting approval,
 * and the composer applies it only after the user says yes. Still no server-side edit channel.
 */
export function useStrategyChatStream() {
  const api = useApiClient();
  const qc = useQueryClient();
  const [isPending, setPending] = useState(false);
  const start = useCallback(async (
    args: StrategyChatArgs,
    signal?: AbortSignal,
  ): Promise<StrategyChatStreamResponse> => {
    setPending(true);
    try {
      // A holder object, not two `let`s: the events land inside the callback, and TypeScript would
      // otherwise narrow plain locals to `null` past the await.
      const seen: { reply: StrategyChatStreamResponse | null; failure: { code: string; detail: string } | null } =
        { reply: null, failure: null };
      await api.postStream('/api/v1/ai-backtest/chat', chatBody(args), e => {
        // Only the terminal events are read: `heartbeat` and any other event name fall through.
        if (e.event === 'reply') seen.reply = JSON.parse(e.data) as StrategyChatStreamResponse;
        else if (e.event === 'error') seen.failure = JSON.parse(e.data) as { code: string; detail: string };
      }, { signal });
      if (seen.failure) throw new ApiError(502, 'stream', { detail: seen.failure.detail, type: seen.failure.code });
      if (!seen.reply) throw new StreamEndedError();
      // The persisted conversation is this hook's cache to keep: append the turn the BE just saved so
      // a later refetch and the composer's own list agree without a round trip.
      const saved = seen.reply;
      if (args.strategyId && saved.turnId) {
        const record: ChatTurnRecord = {
          id: saved.turnId, question: args.message, reply: saved.reply,
          proposal: saved.proposal ?? null, decisions: null, questions: saved.questions ?? null,
          createdAt: new Date().toISOString(),
        };
        qc.setQueryData<ChatTurnRecord[]>(aiBacktestKeys.chatTurns(args.strategyId),
          old => [...(old ?? []).filter(t => t.id !== record.id), record]);
      }
      return saved;
    } finally {
      setPending(false);
      void qc.invalidateQueries({ queryKey: aiBacktestKeys.credits });
    }
  }, [api, qc]);
  return { start, isPending };
}

/** The persisted conversation for a saved strategy (2026-09-02) — what modify mode replays. */
export function useAiChatTurns(strategyId: string | undefined) {
  const api = useApiClient();
  return useQuery({
    queryKey: aiBacktestKeys.chatTurns(strategyId ?? ''),
    enabled: !!strategyId,
    queryFn: () => api.get<ChatTurnRecord[]>(`/api/v1/ai-backtest/strategies/${strategyId}/chat`),
  });
}

/** Records the user's answer for ONE box of a proposed change (2026-09-02; per box since 2026-09-06).
 * Applying the change stays in the composer. */
export function useDecideChatProposal() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { strategyId: string; turnId: string; target: StrategyChatProposalChange['target']; decision: ChatDecision }) =>
      api.post<void>(`/api/v1/ai-backtest/strategies/${args.strategyId}/chat/${args.turnId}/decision`,
        { target: args.target, decision: args.decision }),
    // That tab stops asking immediately; the server row is the durable copy.
    onMutate: args => qc.setQueryData<ChatTurnRecord[]>(aiBacktestKeys.chatTurns(args.strategyId),
      old => (old ?? []).map(t => (t.id === args.turnId
        ? { ...t, decisions: { ...(t.decisions ?? {}), [args.target]: args.decision } }
        : t))),
  });
}

export function useCreateStrategy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveStrategyRequest) =>
      api.post<StrategySavedResponse>('/api/v1/ai-backtest/strategies', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.root }),
  });
}

/** Modify + re-run — APPENDS a new run (spec §1D.2). */
export function useUpdateStrategy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SaveStrategyRequest }) =>
      api.put<StrategySavedResponse>(`/api/v1/ai-backtest/strategies/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.root }),
  });
}

/** Inline title/description edit on the results page (no re-run, no credit). */
export function useRenameStrategy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, description }: { id: string; name: string; description: string }) =>
      api.patch<void>(`/api/v1/ai-backtest/strategies/${id}/meta`, { name, description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.root }),
  });
}

export function useDeleteStrategy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/ai-backtest/strategies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiBacktestKeys.strategies }),
  });
}

export type ExportLanguage = 'pine' | 'lipi' | 'mql5';
/** The transpiled script and (BE phase 5b of the ATR day-levels plan, 2026-10-21) the strategy's own
 * caveats: deterministic lines the dialog shows above the platform's static list, each only when it
 * applies. Optional on the wire so an older BE still exports. */
export interface ExportCodeResponse { fileName: string; language: string; code: string; caveats?: string[] }

/** Deterministic code export (no AI, no credit): the saved DSL transpiled to platform code —
 * Pine Script v6 (TradingView), LipiScript (GoCharting — signals & alerts only) or an MQL5
 * Expert Advisor (MetaTrader 5). A mutation (not a query) — fetched on click, nothing cached. */
export function useExportStrategy(id: string | undefined) {
  const api = useApiClient();
  return useMutation({
    mutationFn: (lang: ExportLanguage) =>
      api.get<ExportCodeResponse>(`/api/v1/ai-backtest/strategies/${id}/export?lang=${lang}`),
  });
}
